import { useEffect, useRef, useState } from "react";

import {
  Alert,
  Box,
  Skeleton,
  Snackbar,
  Stack,
  Switch,
  Tooltip,
  Typography,
} from "@mui/material";

import { ErrorTypography } from "components/ErrorTypography";
import { EvotruxDivider } from "components/EvotruxDivider";
import {
  useRoleContext,
  useShipperEmailNotificationPreferences,
  useShipperEmailNotificationPreferencesUpdate,
} from "hooks";

import type {
  ShipperEmailNotificationPreference,
  ShipperEmailNotificationPreferenceKey,
} from "hooks/useShipperEmailNotificationPreferences";

interface ShipperNotificationToggle {
  preferenceKey: ShipperEmailNotificationPreferenceKey;
  label: string;
  tooltip: string;
  caption: string;
}

// Curated on purpose: the backend enum carries keys this list does not render.
// Adding a preference/group later is a one-line change here, matching the
// backend's data-driven maps of preference key to the emails it governs. Labels
// are phrased positively so "on" always means "notify me".
const TOGGLES: ShipperNotificationToggle[] = [
  {
    preferenceKey: "SHIPMENT_SHARED",
    label: "Email me when a shipment is shared with me",
    tooltip:
      "When on, you receive an email whenever a teammate shares a shipment with you.",
    caption: "Turn off to stop the share email.",
  },
  {
    preferenceKey: "MICRO_REVIEW_REMINDER",
    label: "Email me reminders to rate a carrier's service",
    tooltip:
      "When on, you receive an occasional email asking whether you were happy with a carrier's service on a delivered shipment.",
    caption: "Turn off to stop these reminder emails.",
  },
];

const toEnabledByKey = (
  preferences: ShipperEmailNotificationPreference[],
): Record<string, boolean> =>
  Object.fromEntries(
    preferences.map((preference) => [
      preference.preference_key,
      preference.email_enabled,
    ]),
  );

export function ShipperEmailNotificationPreferences() {
  const { role } = useRoleContext();
  const { preferences, error: fetchError } =
    useShipperEmailNotificationPreferences();
  const [enabledByKey, setEnabledByKey] = useState<Record<
    string,
    boolean
  > | null>(null);
  const [showUpdatedToast, setShowUpdatedToast] = useState(false);
  const hasSeededRef = useRef(false);
  // Holds the key + pre-toggle value for the in-flight write so a failure
  // reverts only that key, without touching keys persisted by earlier toggles.
  const pendingRevertRef = useRef<{
    preferenceKey: ShipperEmailNotificationPreferenceKey;
    previousValue: boolean;
  } | null>(null);

  const {
    updatePreference,
    error: updateError,
    loading: updateLoading,
  } = useShipperEmailNotificationPreferencesUpdate({
    onUpdateSuccess: () => setShowUpdatedToast(true),
    onUpdateFailure: () => {
      const revert = pendingRevertRef.current;
      if (revert != null) {
        setEnabledByKey((previous) => ({
          ...(previous ?? {}),
          [revert.preferenceKey]: revert.previousValue,
        }));
      }
    },
  });

  useEffect(() => {
    if (preferences != null && !hasSeededRef.current) {
      setEnabledByKey(toEnabledByKey(preferences));
      hasSeededRef.current = true;
    }
  }, [preferences]);

  if (role?.type !== "SHIPPER") {
    return null;
  }

  const isLoading = preferences == null || enabledByKey === null;

  const handleToggle = (
    preferenceKey: ShipperEmailNotificationPreferenceKey,
    emailEnabled: boolean,
  ) => {
    pendingRevertRef.current = { preferenceKey, previousValue: !emailEnabled };
    setEnabledByKey((previous) => ({
      ...(previous ?? {}),
      [preferenceKey]: emailEnabled,
    }));
    updatePreference(preferenceKey, emailEnabled);
  };

  return (
    <Box m="1rem">
      <Typography variant="h4">Notification Preferences</Typography>
      <EvotruxDivider />
      <Box mt="1rem">
        {isLoading ? (
          <Stack spacing={2}>
            {TOGGLES.map((toggle) => (
              <Skeleton
                key={toggle.preferenceKey}
                variant="rounded"
                width="100%"
                height={48}
              />
            ))}
          </Stack>
        ) : (
          <Stack spacing={2}>
            {TOGGLES.map((toggle) => (
              <Box key={toggle.preferenceKey}>
                <Stack direction="row" alignItems="center" spacing={1}>
                  {/* disableInteractive keeps the popper out of the hit test: it
                  renders below its own switch, over the next toggle in the list,
                  and an interactive popper would swallow that toggle's click. */}
                  <Tooltip title={toggle.tooltip} disableInteractive>
                    <Switch
                      inputProps={
                        {
                          "data-testid": `shipper-email-pref-${toggle.preferenceKey}-switch`,
                        } as React.InputHTMLAttributes<HTMLInputElement>
                      }
                      checked={enabledByKey?.[toggle.preferenceKey] ?? true}
                      onChange={(event) =>
                        handleToggle(toggle.preferenceKey, event.target.checked)
                      }
                      disabled={updateLoading}
                    />
                  </Tooltip>
                  <Typography variant="body1">{toggle.label}</Typography>
                </Stack>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ ml: 7 }}
                >
                  {toggle.caption}
                </Typography>
              </Box>
            ))}
          </Stack>
        )}
        <ErrorTypography error={fetchError} />
        <ErrorTypography error={updateError} />
      </Box>
      <Snackbar
        open={showUpdatedToast}
        onClose={() => setShowUpdatedToast(false)}
        autoHideDuration={3000}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={() => setShowUpdatedToast(false)}
          severity="success"
          variant="filled"
          sx={{ width: "100%" }}
        >
          Updated
        </Alert>
      </Snackbar>
    </Box>
  );
}
